import { useCallback, useEffect, useState } from "react";
import { desktop } from "@/controller";
import {
  openDesktopMicrophone,
  openDesktopSystemAudio,
} from "@/recording/desktop-audio";
import { useUISlice } from "@/models/useUISlice";
import type {
  DesktopHostInfo,
  DesktopPermissionStatus,
  DesktopSettings,
  DesktopUpdateStatus,
} from "@/clients/desktop.client";
import { DESKTOP_HOST_CONTRACT_VERSION } from "@/clients/desktop.client";

const effectivePermissions = (
  permissions?: DesktopPermissionStatus,
): DesktopPermissionStatus | undefined => {
  if (!permissions) return undefined;
  const denied = useUISlice.getState().desktopAudioPermissionDenied;
  return {
    ...permissions,
    ...(denied?.microphone ? { microphone: "denied" as const } : {}),
    ...(denied?.systemAudio && permissions.systemAudio !== "unsupported"
      ? { systemAudio: "denied" as const }
      : {}),
  };
};

export const useDesktopHost = (enabled = true) => {
  useUISlice((state) => state.desktopAudioPermissionDenied);
  const [info, setInfo] = useState<DesktopHostInfo>();
  const [settings, setSettings] = useState<DesktopSettings>();
  const [permissions, setPermissions] = useState<DesktopPermissionStatus>();
  const [update, setUpdate] = useState<DesktopUpdateStatus>({ state: "idle" });
  const [busy, setBusy] = useState(false);
  const [operationFailed, setOperationFailed] = useState(false);
  const refreshPermissions = useCallback(async () => {
    const next = await desktop.getPermissions();
    if (next) setPermissions(next);
    return effectivePermissions(next);
  }, []);

  useEffect(() => {
    if (!enabled || !desktop.isAvailable) return;
    let disposed = false;
    void Promise.all([
      desktop.getInfo(),
      desktop.getSettings(),
      desktop.getPermissions(),
      desktop.getUpdateStatus(),
    ])
      .then(([nextInfo, nextSettings, nextPermissions, nextUpdate]) => {
        if (disposed) return;
        if (
          nextInfo &&
          nextInfo.contractVersion !== DESKTOP_HOST_CONTRACT_VERSION
        ) {
          // Acknowledge only the update screen so the old shell does not replace
          // it with its timed recovery page. No capture UI mounts on that host.
          void desktop.ready(nextInfo.contractVersion).catch(() => undefined);
          void desktop.setIndicator({ state: "hidden" }).catch(() => undefined);
        }
        setInfo(nextInfo);
        setSettings(nextSettings);
        setPermissions(nextPermissions);
        if (nextUpdate) setUpdate(nextUpdate);
      })
      .catch(() => {
        if (!disposed) setOperationFailed(true);
      });

    const handleFocus = () => {
      void refreshPermissions().catch(() => undefined);
    };
    // action-id: lifecycle.desktop.permissionRefresh
    window.addEventListener("focus", handleFocus);
    const offUpdate = desktop.onUpdateStatus(setUpdate);
    return () => {
      disposed = true;
      window.removeEventListener("focus", handleFocus);
      offUpdate();
    };
  }, [enabled, refreshPermissions]);

  const updateSettings = useCallback(
    async (patch: Partial<DesktopSettings>) => {
      setBusy(true);
      setOperationFailed(false);
      try {
        const next = await desktop.updateSettings(patch);
        if (next) setSettings(next);
        return next;
      } catch {
        setOperationFailed(true);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const requestPermission = useCallback(
    async (permission: "microphone" | "accessibility" | "systemAudio") => {
      setBusy(true);
      setOperationFailed(false);
      try {
        if (permission === "systemAudio") {
          const current = await desktop.getPermissions();
          if (current?.systemAudio === "unsupported") {
            setPermissions(current);
            return current;
          }
          try {
            const stream = await openDesktopSystemAudio();
            stream.getTracks().forEach((track) => track.stop());
          } catch {
            useUISlice.setState((state) => ({
              desktopAudioPermissionDenied: {
                ...state.desktopAudioPermissionDenied,
                systemAudio: true,
              },
            }));
            await desktop.openPermissionSettings("systemAudio");
            setOperationFailed(true);
            await refreshPermissions();
            return undefined;
          }
          useUISlice.setState((state) => ({
            desktopAudioPermissionDenied: {
              ...state.desktopAudioPermissionDenied,
              systemAudio: false,
            },
          }));
          return await refreshPermissions();
        }
        const next = await desktop.requestPermission(permission);
        if (next) setPermissions(next);
        if (permission === "microphone" && next?.microphone === "granted") {
          try {
            const stream = await openDesktopMicrophone();
            stream.getTracks().forEach((track) => track.stop());
          } catch {
            useUISlice.setState((state) => ({
              desktopAudioPermissionDenied: {
                ...state.desktopAudioPermissionDenied,
                microphone: true,
              },
            }));
            setOperationFailed(true);
            await desktop.openPermissionSettings("microphone");
            return undefined;
          }
          useUISlice.setState((state) => ({
            desktopAudioPermissionDenied: {
              ...state.desktopAudioPermissionDenied,
              microphone: false,
            },
          }));
        }
        if (
          next &&
          ((permission === "microphone" && next.microphone !== "granted") ||
            (permission === "accessibility" &&
              next.accessibility !== "granted"))
        ) {
          await desktop.openPermissionSettings(permission);
        }
        return effectivePermissions(next);
      } catch {
        setOperationFailed(true);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [refreshPermissions],
  );

  const checkForUpdates = useCallback(async () => {
    setOperationFailed(false);
    try {
      await desktop.checkForUpdates();
    } catch {
      setOperationFailed(true);
    }
  }, []);

  const installUpdate = useCallback(async () => {
    setOperationFailed(false);
    try {
      return await desktop.installUpdate();
    } catch {
      setOperationFailed(true);
      return false;
    }
  }, []);

  return {
    info,
    needsUpdate: Boolean(
      info && info.contractVersion !== DESKTOP_HOST_CONTRACT_VERSION,
    ),
    settings,
    permissions: effectivePermissions(permissions),
    update,
    busy,
    operationFailed,
    updateSettings,
    requestPermission,
    refreshPermissions,
    checkForUpdates,
    installUpdate,
  };
};

export type DesktopHostModel = ReturnType<typeof useDesktopHost>;
